import {identity} from '@packages/utilities/fp/utils';
import {type Expression} from '@packages/systems/wfdl-ast';
import type {
  ReportType,
  Migrations,
  Patch,
  NodePatch,
  FlowPatch,
  Context,
} from './types';
import {hasFlattenedOverrides} from '@packages/systems/core/render';
import {processNodes} from '@packages/systems/core/utils/JsonDomUtils';
import type {JSONNode} from '@packages/shared/types/JSONNode';

import {
  applyPatchToDefaultValues,
  detectRegressionsDefaultValues,
} from './utils/DefaultValueMigrationUtils';
import {
  applyPatchToDefaultData,
  detectRegressionsDefaultData,
} from './utils/DefaultDataMigrationUtils';
import {
  shouldApplyPatchToDefaultValues,
  shouldApplyPatchToDefaultData,
} from './utils/PatchVersionUtils';
import {
  enhanceExtraContext,
  emptyEnhancedExtraContext,
  type EnhancedExtraContext,
  type ExtraContext,
} from './ExtraContext';

import patch001_impossibleVProp from './patches/001-impossibleVProp';
import patch002_impossibleChildren from './patches/002-impossibleChildren';
import patch003_dynBoundImageWithStaticSrc from './patches/003-dynBoundImageWithStaticSrc';
import patch004_unnecessaryValidationsInDynBindingPaths from './patches/004-unnecessaryValidationsInDynBindingPaths';
import patch005_childIdsReferenceMissingNodes from './patches/005-childIdsReferenceMissingNodes';
import patch006_gridTypeFull from './patches/006-gridTypeFull';
import patch007_linksWithIsCurrent from './patches/007-linksWithIsCurrent';
import patch008_symbolRootWithOrderProp from './patches/008-symbolRootWithOrderProp';
import patch009_staticEmbedVMismatchesMetaHtml from './patches/009-staticEmbedVMismatchesMetaHtml';
import patch010_compilableEmbedWithStaticInnerHtml from './patches/010-compilableEmbedWithStaticInnerHtml';
import patch011_preserve3dWithFalseVal from './patches/011-preserve3dWithFalseVal';
import patch012_clearFixWithFalseVal from './patches/012-clearFixWithFalseVal';
import patch013_navBarWithDataContainProp from './patches/013-navBarWithDataContainProp';
import patch014_legacyLightboxImages from './patches/014-legacyLightboxImages';
import patch015_legacyLightboxEmbeds from './patches/015-legacyLightboxEmbeds';
import patch016_legacyLightboxGroup from './patches/016-legacyLightboxGroup';
import patch017_dropdownWithDropdownType from './patches/017-dropdownWithDropdownType';
import patch018_innerHTMLBindingWithLegacyOptionFilter from './patches/018-innerHTMLBindingWithLegacyOptionFilter';
import patch019_sliderIconArrows from './patches/019-sliderIconArrows';
import patch020_textHolderWithLegacyVProp from './patches/020-textHolderWithLegacyVProp';
import patch023_simpleTextContentNodesWithDisallowedTags from './patches/023-simpleTextContentNodesWithDisallowedTags';
import patch024_dynBoundHtmlWithoutDataTextProp from './patches/024-dynBoundHtmlWithoutDataTextProp';
import patch025_videoEmbedWithTextBlockProps from './patches/025-videoEmbedWithTextBlockProps';
import patch026_symbolInstanceWithoutDivTagname from './patches/026-symbolInstanceWithoutDivTagname';
import patch027_dynBoundVideoWithStaticContent from './patches/027-dynBoundVideoWithStaticContent';
import patch028_invalidTagStyleBlockIdsInClasses from './patches/028-invalidTagStyleBlockIdsInClasses';
import patch029_rootSymbolOnPage from './patches/029-rootSymbolOnPage';
import patch030_typographicBlockWithoutDataTextProp from './patches/030-typographicBlockWithoutDataTextProp';
import patch031_symbolInstanceInSymbolRoot from './patches/031-symbolInstanceInSymbolRoot';
import patch032_rteFigureWithUnitlessMaxWidth from './patches/032-rteFigureWithUnitlessMaxWidth';
import patch033_symbolInstanceOfDeletedSymbol from './patches/033-symbolInstanceOfDeletedSymbol';
import patch034_symbolsTrackingCopiesOnDeletedPages from './patches/034-symbolsTrackingCopiesOnDeletedPages';
import patch035_innerHTMLBindingWithLegacyDateFilter from './patches/035-innerHTMLBindingWithLegacyDateFilter';
import patch036_emailLinkWithUnencodedSubject from './patches/036-emailLinkWithUnencodedSubject';
import patch037_emailPhoneMissingFilter from './patches/037-emailPhoneMissingFilter';
import patch038_cleanUpCorruptDynamicListFilter from './patches/038-cleanUpCorruptDynamicListFilter';
import patch039_moveSearchBinding from './patches/039-moveSearchBinding';
import patch040_fixCompilableHtmlEmbed from './patches/040-fixCompilableHtmlEmbed';
import patch041_migrateDetailPageFilters from './patches/041-migrateDetailPageFilters';
import patch042_migrateRichTextDetailPageLinks from './patches/042-migrateRichTextDetailPageLinks';
import patch043_patchDetailPageLinks from './patches/043-patchDetailPageLinks';
import patch044_patchRichTextDetailPageLinks from './patches/044-patchRichTextDetailPageLinks';
import patch045_removeFormFileUploadV1 from './patches/045-removeFormFileUploadV1';
import patch046_patchDataWHrefItemRefLinks from './patches/046-patchDataWHrefItemRefLinks';
import patch047_moveCommerceBindings from './patches/047-moveCommerceBindings';
import patch048_moveCommerceConditions from './patches/048-moveCommerceConditions';
import patch049_commerceStripe from './patches/049-commerceStripe';
import patch050_refCustomAttribute from './patches/050-refCustomAttribute';
import patch051_nukeAttrDataNodeType from './patches/051-nukeAttrDataNodeType';
import patch052_updateLegacyCommerceElementType from './patches/052-updateLegacyCommerceElementType';
import patch053_patchNodesRequiringDataTag from './patches/053-patchNodesRequiringDataTag';
import patch054_updateFieldGroup from './patches/054-updateFieldGroup';
import patch055_addOptionListToCart from './patches/055-addOptionListToCart';
import patch056_addOptionListToOrderItems from './patches/056-addOptionListToOrderItems';
import patch057_gPlusWithLegacyAnnotationWidgetDataProp from './patches/057-gPlusWithLegacyAnnotationWidgetDataProp';
import patch058_addQuickCheckoutToCart from './patches/058-addQuickCheckoutToCart';
import patch059_stringifyNodeDataIds from './patches/059-stringifyNodeDataIds';
import patch060_patchAttrsToDataLink from './patches/060-patchAttrsToDataLink';
import patch061_undefinedSymbolNames from './patches/061-undefinedSymbolNames';
import patch062_selectNodesWithStringHtml from './patches/062-selectNodesWithStringHtml';
import patch064_removeDataLinkFromDynBoundLink from './patches/064-removeDataLinkFromDynBoundLink';
import patch065_changeLightboxWrapperDynBindingPropFromSrcToMedia from './patches/065-changeLightboxWrapperDynBindingPropFromSrcToMedia';
import patch066_rteWithHrefAttributeAndDataLink from './patches/066-rteWithHrefAttributeAndDataLink';
import patch067_changeAnimationElementToNewUnionType from './patches/067-changeAnimationElementToNewUnionType';
import patch068_twitterToDataTypes from './patches/068-twitterToDataTypes';
import patch069_queryFieldsToList from './patches/069-queryFieldsToList';
import patch070_symbolFieldSystemBindUIVariant from './patches/070-symbolFieldSystemBindUIVariant';
import patch071_dropPluginComponent from './patches/071-dropPluginComponent';
import patch072_removeLegacyBindingSubpath from './patches/072-removeLegacyBindingSubpath';
import patch073_migrateDOMBrToLineBreak from './patches/073-migrateDOMBrToLineBreak';
import patch074_removeClassesFromLineBreak from './patches/074-removeClassesFromLineBreak';
import patch075_mapWidgetToDataTypes from './patches/075-mapWidgetToDataTypes';
import patch076_dropdownToDataTypes from './patches/076-dropdownToDataTypes';
import patch077_removeRedundantDataHover from './patches/077-removeRedundantDataHover';
import patch078_migrateOrderItemsSkuPriceConditions from './patches/078-migrateOrderItemsSkuPriceConditions';
import patch079_lightboxWrapperToDataTypes from './patches/079-lightboxWrapperToDataTypes';
import patch080_removeStyledAs from './patches/080-removeStyledAs';
import patch081_addUserAccountForm from './patches/081-addUserAccountForm';
import patch082_convertCommercePaypalCheckoutErrorMessageData from './patches/082-convertCommercePaypalCheckoutErrorMessageData';
import patch083_flattenOverrides from './patches/083-flattenOverrides';
import patch084_migrateMultiTextEditNodes from './patches/084-migrateMultiTextEditNodes';
import patch085_migrateDivBlockToBlock from './patches/085-migrateDivBlockToBlock';
import patch086_formWaitField from './patches/086-formWaitField';
import patch087_imageDataLoss from './patches/087-imageDataLoss';
import patch088_linkDataLoss from './patches/088-linkDataLoss';
import patch089_fixImageWidthAndHeight from './patches/089-fixImageWidthAndHeight';
import patch090_imageAltTypeChange from './patches/090-imageAltTypeChange';
import patch091_dynamicPageLinkFromSlugToFullSlug from './patches/091-dynamicPageLinkFromSlugToFullSlug';
import patch092_videoCMSBindingsToUB from './patches/092-videoCMSBindingsToUB';
import patch093_formBlockLabelWithUsersProp from './patches/093-formBlockLabelWithUsersProp';
import patch094_fixDataTypeJson from './patches/094-fixDataTypeJson';
import patch095_lightboxMediaCMSLegacyBindingsToUB from './patches/095-lightboxMediaCMSLegacyBindingsToUB';
import patch096_richTextCMSBindingsToUB from './patches/096-richTextCMSBindingsToUB';
import patch097_linkModeFixes from './patches/097-linkModeFixes';
import patch098_imageResponsiveAttributes from './patches/098-imageResponsiveAttributes';
import patch099_imageCMSBindingsToUB from './patches/099-imageCMSBindingsToUB';
import patch100_plainTextCMSLegacyBindingsToUB from './patches/100-plainTextCMSBindingsToUB';
import patch101_cartFormLabelWithPasswordPageFlagTrue from './patches/101-cartFormLabelWithPasswordPageFlagTrue';
import patch102_missingDataDynType from './patches/102-missingDataDynType';
import patch103_dynamoWrapperSortChange from './patches/103-dynamoWrapperSortChange';
import patch104_missingDynListNodeType from './patches/104-missingDynListNodeType';
import patch105_dynWrapperFilterFor from './patches/105-dynWrapperFilterFor';
import patch106_curatedCollectionList from './patches/106-curatedCollectionList';
import patch107_localeListEmptyNonTextHolder from './patches/107-localeListEmptyNonTextHolder';
import patch108_legacyVisibilityConditionsToConditionals from './patches/108-legacyVisibilityConditionsToConditionals';
import patch109_moveCommerceAddToCartLoadingText from './patches/109-moveCommerceAddToCartLoadingText';
import patch110_devlinkRuntimePropsToAttributes from './patches/110-devlinkRuntimePropsToAttributes';
import patch111_defaultDataFromDataType from './patches/111-defaultDataFromDataType';

// Used to log modifications
const reportChange =
  (logFn: (arg1: ReportType) => void) =>
  ({msg, meta}: ReportType) => {
    logFn && logFn({msg, meta});
  };

// Used to log non-show-stopper warnings
const reportWarning =
  (logFn: (arg1: ReportType) => void) => (msg: any, warning: any) => {
    logFn && logFn({msg, meta: {warning}});
  };

// A single migration pass visits every node in the site tree. Identical
// warnings can fire for every instance of a symbol — potentially thousands of
// times per site. Deduplicating by warning string keeps logs readable and avoids
// flooding downstream consumers with identical reports.
const DEDUPED_MSGS = new Set([
  'MalformedDefaultValue',
  'MalformedDefaultDataPatch',
  'DevlinkRuntimePropsOrphanedInstance',
]);

const getWarningDeduplicationKey = (msg: string, warning: string) => {
  if (msg === 'MalformedDefaultValue' || msg === 'MalformedDefaultDataPatch') {
    return warning.replace(/ on node "[^"]+"/, ' on node');
  }

  if (msg === 'DevlinkRuntimePropsOrphanedInstance') {
    return warning.replace(/^Symbol instance "[^"]+"/, 'Symbol instance');
  }

  return warning;
};

const dedupeWarnings = (logFn: (arg1: ReportType) => void) => {
  const seen = new Set<string>();

  return (report: ReportType) => {
    if (!DEDUPED_MSGS.has(report.msg)) {
      logFn(report);
      return;
    }

    const {warning} = report.meta;

    if (typeof warning !== 'string') {
      logFn(report);
      return;
    }

    const key = `${report.msg}:${getWarningDeduplicationKey(
      report.msg,
      warning
    )}`;

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    logFn(report);
  };
};

export const getAppVersion = (migrations: Migrations): number =>
  migrations.length;

export const nodePatch = (patch: NodePatch): Patch => ({
  node: patch,
  flow: identity,
  preventStaleClientsLoadingPatchedNodes: false,
});

/**
 * Registers a node patch that needs aggressive stale client management
 *
 * WARNING! Use this if and only if you are absolutely certain your node
 * patch needs to prevent stale clients from encountering patched nodes.
 *
 * Registering a patch with this instead of the regular `nodePatch` utility
 * triggers an extended and more aggressive stale client management for clients
 * that are on versions that are missing the code for this patch.
 *
 * The more aggressive stale client management extends our normal stale client
 * management routine by _immediately_ preventing stale Designer and Editor
 * clients from
 * - loading the client (if they have a stale client in the browser cache)
 * - switching pages
 * - creating new CMS items
 * - updating existing CMS items
 *
 * The objective is to
 * - prevent stale clients from encountering patched node data
 * - prevent stale clients from saving stale node data to CMS Rich Text
 *
 * Use this method for registering patches if and only if
 * - your patch changes node data shape in ways that stale clients can't handle
 * - your patch requires migrating CMS Rich Text data separately
 *
 * Please consider reaching out to node migration experts before using this.
 */

function __DANGER__nodePatchWithAggressiveStaleClientManagement(
  patch: NodePatch
): Patch {
  return {
    node: patch,
    flow: identity,
    preventStaleClientsLoadingPatchedNodes: true,
  };
}

export const flowPatch = (patch: FlowPatch): Patch => ({
  node: null,
  flow: patch,
  preventStaleClientsLoadingPatchedNodes: false,
});

const getFlowPatch = (patch: Patch): FlowPatch => patch.flow;

// NOTE: We transitioned from semver to int for versioning and the way
// we group patches per version. This maps the old semver versions to
// the new int versions so the system still runs the correct patches for old sites.
export const LEGACY_VERSIONS: Readonly<Record<string, number>> = {
  // NOTE: 3.0.0 was the first version enforced site-wide,
  // but notably introduced the rte-next 1.x content node expansion.
  '3.0.0': 20,
  '3.0.1': 44,
  '3.0.2': 45,
  '3.0.3': 46,
  '3.0.4': 47,
  '3.0.5': 48,
  '3.0.6': 49,
  '3.0.7': 50,
  '3.0.8': 51,
  '3.0.9': 52,
  '3.0.10': 53,
  '3.0.11': 54,
  '3.0.12': 55,
  '3.0.13': 56,

  '3.1.0': 58,
  '3.1.1': 60,
  '3.1.2': 61,
  '3.1.3': 62,

  '4.0.0': 64,
  '4.0.1': 65,
  '4.0.2': 66,
  '4.0.3': 69,
  '4.0.4': 70,
  '4.0.5': 71,
  '4.0.6': 72,
  '4.0.7': 73,
  '4.0.8': 74,
  '4.0.9': 75,
  '4.0.10': 76,
  '4.0.11': 77,
  '4.0.12': 78,
  '4.0.13': 79,
  '4.0.14': 80,
  '4.0.15': 81,
  '4.0.16': 82,
  '4.0.17': 83,
  '4.0.18': 84,
  '4.0.19': 85,
  '4.0.20': 86,
  '4.0.21': 87,
};

export const MIGRATIONS: Migrations = [
  // a site with version 0 needs all the patches including the one at index 0
  nodePatch(patch001_impossibleVProp), // version 1
  nodePatch(patch002_impossibleChildren),
  nodePatch(patch003_dynBoundImageWithStaticSrc),
  nodePatch(patch004_unnecessaryValidationsInDynBindingPaths),
  nodePatch(patch005_childIdsReferenceMissingNodes),
  nodePatch(patch006_gridTypeFull),
  nodePatch(patch007_linksWithIsCurrent),
  nodePatch(patch008_symbolRootWithOrderProp),
  nodePatch(patch009_staticEmbedVMismatchesMetaHtml),

  nodePatch(patch010_compilableEmbedWithStaticInnerHtml),
  nodePatch(patch011_preserve3dWithFalseVal),
  nodePatch(patch012_clearFixWithFalseVal),
  nodePatch(patch013_navBarWithDataContainProp),
  nodePatch(patch014_legacyLightboxImages),
  nodePatch(patch015_legacyLightboxEmbeds),
  nodePatch(patch016_legacyLightboxGroup),
  nodePatch(patch017_dropdownWithDropdownType),
  nodePatch(patch018_innerHTMLBindingWithLegacyOptionFilter),
  nodePatch(patch019_sliderIconArrows),

  nodePatch(patch020_textHolderWithLegacyVProp),
  // Earlier migrations were grouped by tree iterations and
  // second tree iteration reapplied the patches from the first iteration
  nodePatch(patch001_impossibleVProp),
  nodePatch(patch002_impossibleChildren),
  nodePatch(patch003_dynBoundImageWithStaticSrc),
  nodePatch(patch004_unnecessaryValidationsInDynBindingPaths),
  nodePatch(patch005_childIdsReferenceMissingNodes),
  nodePatch(patch006_gridTypeFull),
  nodePatch(patch007_linksWithIsCurrent),
  nodePatch(patch008_symbolRootWithOrderProp),
  nodePatch(patch009_staticEmbedVMismatchesMetaHtml),

  nodePatch(patch010_compilableEmbedWithStaticInnerHtml),
  nodePatch(patch011_preserve3dWithFalseVal),
  nodePatch(patch012_clearFixWithFalseVal),
  nodePatch(patch013_navBarWithDataContainProp),
  nodePatch(patch014_legacyLightboxImages),
  nodePatch(patch015_legacyLightboxEmbeds),
  nodePatch(patch016_legacyLightboxGroup),
  nodePatch(patch017_dropdownWithDropdownType),
  nodePatch(patch018_innerHTMLBindingWithLegacyOptionFilter),
  nodePatch(patch019_sliderIconArrows),

  nodePatch(patch023_simpleTextContentNodesWithDisallowedTags),
  nodePatch(patch024_dynBoundHtmlWithoutDataTextProp),
  nodePatch(patch025_videoEmbedWithTextBlockProps),
  nodePatch(patch027_dynBoundVideoWithStaticContent),
  nodePatch(patch020_textHolderWithLegacyVProp),
  nodePatch(patch028_invalidTagStyleBlockIdsInClasses),
  nodePatch(patch029_rootSymbolOnPage),
  nodePatch(patch030_typographicBlockWithoutDataTextProp),
  nodePatch(patch031_symbolInstanceInSymbolRoot),
  nodePatch(patch032_rteFigureWithUnitlessMaxWidth),

  nodePatch(patch035_innerHTMLBindingWithLegacyDateFilter),
  nodePatch(patch036_emailLinkWithUnencodedSubject),
  nodePatch(patch037_emailPhoneMissingFilter),
  // This patch was originally used in 3.0.7, but the root issue was
  // still reproable in the designer. Now that the root issue has been
  // fixed in https://github.com/webflow/webflow/pull/12472 we're
  // re-applying the patch with a version bump
  nodePatch(patch035_innerHTMLBindingWithLegacyDateFilter),
  nodePatch(patch038_cleanUpCorruptDynamicListFilter),
  nodePatch(patch039_moveSearchBinding),
  nodePatch(patch040_fixCompilableHtmlEmbed),
  nodePatch(patch041_migrateDetailPageFilters),
  nodePatch(patch042_migrateRichTextDetailPageLinks),
  nodePatch(patch043_patchDetailPageLinks),

  nodePatch(patch044_patchRichTextDetailPageLinks),
  nodePatch(patch045_removeFormFileUploadV1),
  nodePatch(patch046_patchDataWHrefItemRefLinks),
  nodePatch(patch047_moveCommerceBindings),
  nodePatch(patch048_moveCommerceConditions),
  nodePatch(patch049_commerceStripe),
  nodePatch(patch050_refCustomAttribute),
  nodePatch(patch051_nukeAttrDataNodeType),
  nodePatch(patch052_updateLegacyCommerceElementType),
  // We re-introduced some nodes with Ecommerce that have innerHTML but don't have data.text = true
  nodePatch(patch024_dynBoundHtmlWithoutDataTextProp),

  nodePatch(patch053_patchNodesRequiringDataTag),
  nodePatch(patch054_updateFieldGroup),
  nodePatch(patch055_addOptionListToCart),
  nodePatch(patch056_addOptionListToOrderItems),
  nodePatch(patch057_gPlusWithLegacyAnnotationWidgetDataProp),
  nodePatch(patch059_stringifyNodeDataIds),
  nodePatch(patch058_addQuickCheckoutToCart),
  nodePatch(patch060_patchAttrsToDataLink),
  nodePatch(patch061_undefinedSymbolNames),
  nodePatch(patch062_selectNodesWithStringHtml),

  nodePatch(patch064_removeDataLinkFromDynBoundLink),
  nodePatch(patch065_changeLightboxWrapperDynBindingPropFromSrcToMedia),
  nodePatch(patch066_rteWithHrefAttributeAndDataLink),
  nodePatch(patch067_changeAnimationElementToNewUnionType),
  nodePatch(patch068_twitterToDataTypes),
  nodePatch(patch069_queryFieldsToList),
  // This duplicate migration is intentional. We merged 4.0.18 without
  // accounting for stale clients so we get to do it again.
  // https://github.com/webflow/webflow/issues/33049#issuecomment-584858969
  nodePatch(patch068_twitterToDataTypes),
  nodePatch(patch069_queryFieldsToList),
  nodePatch(patch070_symbolFieldSystemBindUIVariant),
  nodePatch(patch071_dropPluginComponent),
  nodePatch(patch072_removeLegacyBindingSubpath),
  // The duplicate migrations of 071 and 072 are intentional. We merged #37838
  // on February 4, 2021, after which not all sites were fully migrated until we
  // shipped #39990 on March 5, 2021. We need to reintroduce the two patches
  // that we had added between these two dates.
  nodePatch(patch071_dropPluginComponent),
  nodePatch(patch072_removeLegacyBindingSubpath),
  nodePatch(patch073_migrateDOMBrToLineBreak),
  nodePatch(patch074_removeClassesFromLineBreak),
  nodePatch(patch075_mapWidgetToDataTypes),
  nodePatch(patch076_dropdownToDataTypes),
  nodePatch(patch077_removeRedundantDataHover),
  nodePatch(patch078_migrateOrderItemsSkuPriceConditions),
  nodePatch(patch079_lightboxWrapperToDataTypes),
  // When we first ran 073, we did not immediately fix the underlying *cause* of
  // DOM atoms appearing in userland content, and so more instances slowly
  // accrued over the next several months.
  //
  // Once we shipped a [fix][1] for the underlying cause, we needed to re-run the
  // same migration in order to fix instances that have appeared in the time
  // period between when we first shipped the migration and finally shipped the
  // fix for the underlying cause.
  //
  // [1]: https://github.com/webflow/webflow/pull/41325
  nodePatch(patch073_migrateDOMBrToLineBreak),
  nodePatch(patch080_removeStyledAs),
  nodePatch(patch081_addUserAccountForm),
  nodePatch(patch082_convertCommercePaypalCheckoutErrorMessageData),
  nodePatch(patch083_flattenOverrides),

  // App Version: 105
  // Patches after this version will include the migration of User Component `defaultValues`:
  nodePatch(patch084_migrateMultiTextEditNodes),

  // This duplicate migration is intentional. We applied it here again in attempt
  // to determine if it's still needed as a Hot Patch. We believe the underlying
  // issue was fixed in https://github.com/webflow/webflow/pull/28784.
  nodePatch(patch028_invalidTagStyleBlockIdsInClasses),

  nodePatch(patch026_symbolInstanceWithoutDivTagname),

  // This duplicate migration is intentional. We applied it here again in attempt
  // to determine if it's still needed as a Hot Patch, which it still is.
  nodePatch(patch066_rteWithHrefAttributeAndDataLink),

  nodePatch(patch033_symbolInstanceOfDeletedSymbol),
  nodePatch(patch085_migrateDivBlockToBlock),
  nodePatch(patch086_formWaitField),
  nodePatch(patch087_imageDataLoss),
  nodePatch(patch088_linkDataLoss),
  nodePatch(patch089_fixImageWidthAndHeight),
  nodePatch(patch090_imageAltTypeChange),
  nodePatch(patch091_dynamicPageLinkFromSlugToFullSlug),
  nodePatch(patch092_videoCMSBindingsToUB),
  nodePatch(patch093_formBlockLabelWithUsersProp),
  nodePatch(patch094_fixDataTypeJson),
  nodePatch(patch095_lightboxMediaCMSLegacyBindingsToUB),
  nodePatch(patch096_richTextCMSBindingsToUB),
  nodePatch(patch097_linkModeFixes),
  nodePatch(patch098_imageResponsiveAttributes),
  nodePatch(patch099_imageCMSBindingsToUB),
  nodePatch(patch100_plainTextCMSLegacyBindingsToUB),
  nodePatch(patch101_cartFormLabelWithPasswordPageFlagTrue),
  nodePatch(patch102_missingDataDynType),
  // This duplicate migration is intentional. We had to skip the patch_102 on
  // certain sites when we first rolled it out. Running it again makes sure
  // all the sites are migrated.
  nodePatch(patch102_missingDataDynType),
  nodePatch(patch103_dynamoWrapperSortChange),
  nodePatch(patch104_missingDynListNodeType),
  nodePatch(patch105_dynWrapperFilterFor),
  nodePatch(patch106_curatedCollectionList),
  nodePatch(patch107_localeListEmptyNonTextHolder),
  // This was previously a Hot Patch, but we've promoted it to an official migration.
  // Thus the number of the patch does not follow the pattern.
  nodePatch(patch034_symbolsTrackingCopiesOnDeletedPages),
  // Fix for inc-1099
  nodePatch(patch034_symbolsTrackingCopiesOnDeletedPages),
  // The following three patches were previously Hot Patches, but we've promoted them
  // to official migrations. Thus the numbers of the patches are not sequential.
  nodePatch(patch089_fixImageWidthAndHeight),
  nodePatch(patch066_rteWithHrefAttributeAndDataLink),
  nodePatch(patch002_impossibleChildren),

  nodePatch(patch108_legacyVisibilityConditionsToConditionals),
  nodePatch(patch109_moveCommerceAddToCartLoadingText),
  nodePatch(patch110_devlinkRuntimePropsToAttributes),

  // App Version: 141
  // Patches after this version will additionally be applied to the
  // `defaultData` on User Component roots (see
  // PatchVersionUtils.INCLUDE_DEFAULT_DATA_VERSION and
  // utils/DefaultDataMigrationUtils.ts).
  nodePatch(patch111_defaultDataFromDataType),
];

export const APP_VERSION = getAppVersion(MIGRATIONS);

// The GraphQL response extension key under which the backend embeds its
// APP_VERSION so the renderer can detect rolling-deploy version gaps for
// CMS Rich-Text Fields.
export const BACKEND_APP_VERSION_EXTENSION_KEY = 'backendAppVersion';

// Request header the renderer sends to the CMS GraphQL API to receive CMS
// Rich-Text Field values versioned: the `{version, nodes}` envelope as written
// (or a bare array), with no boundary patch. The renderer then patches them
// itself with the page's published components, which are the components the
// page renders with.
export const VERSIONED_CMS_RTFS_HEADER = 'x-wf-versioned-cms-rtfs';

// The GraphQL response extension the backend sets to `true` when it honored
// `VERSIONED_CMS_RTFS_HEADER` (opt-in present, the runtime-patch kill switch
// on, and the `VERSIONED_CMS_RTFS` gate on). The renderer patches only when it
// sees this, so both switches stay authoritative without the renderer
// evaluating feature flags.
export const VERSIONED_CMS_RTFS_EXTENSION_KEY = 'versionedCmsRtfs';

/**
 * The floor version that RTF (CMS Rich Text Field) content is treated as
 * already having been migrated through: both bare-array values (implicitly)
 * and versioned `{version, nodes}` envelopes (whose own stored `version` is
 * clamped up to this floor) never migrate from below it. Components didn't
 * exist in CMS rich-text fields before this snapshot, so no migration below
 * the floor is ever relevant to RTF-embedded content, regardless of what an
 * envelope's own stored version claims.
 *
 * WARNING: this value should never be changed!
 */
export const RTF_PATCH_GENESIS_VERSION = 142;

/**
 * Patch entries added here should be considered short-lived. It's the responsiblity
 * of the patch author to either promote their patch to an official migration in the
 * `MIGRATIONS` array or remove after 2-4 weeks.
 */
const HOT_PATCHES: NodePatch[] = [
  // ex: patch084_migrateMultiTextEditNodes,
];

/**
 * Patch entries added here should be considered short-lived. It's the
 * responsibility of the patch author to review Datadog periodically and to
 * check if there has been any regressions with their patch. If after 2-4 weeks
 * there are no regressions, the author will remove the patch from this array.
 */
export const REGRESSIONS_CHECKS: NodePatch[] = [
  patch111_defaultDataFromDataType,
  // ex: patch084_migrateMultiTextEditNodes,
];

/**
 * Patches are run in the onNode() callback of JsonDomUtils.processNodes. They receive a context
 * object managed by processNodes, and an optional second param that can contain initiated helpers,
 * metadata, or anything else a patch needs.
 *
 * For a fuller explanation of why we migrate jsonNodes and how to write them see
 * /docs/app/data-integrity/site_version.md
 *
 * Patches are used for regression detection, applying hot patches, and applying migrations.
 * - Prefer using processNodes' built in methods for modifying the jsonTree, eg: dropSubtrees
 * - Patches are run on both client and server
 * - Patch names, and logData.msg should describe the state of the detected node, not the fix
 * - Only change the jsonNode or jsonTree from the apply() function
 */

/**
 * Detects migration regressions. Primarily used on the front-end as a safety net. After a patch is used for
 * site-wide migrations parts of the app that were overlooked or subject to a bad git merge could reintroduce
 * legacy jsonNode or jsonTree structures. Instead of waiting for users to notice this sometimes subtle data corruption
 * we can use the patch functions here to detect when and if legacy formats are reintroduced. Once a patch is
 * unlikely to regress it can be removed from the list.
 *
 * NOTE: This should only be used for detection, it should not modify the jsonNodes passed in.
 *
 * @param  {Array}    jsonNodes             a jsonTree (flat array) of jsonNodes
 * @param  {Function} logFn                 a log function for reporting regressions
 * @param  {Object}   enhancedExtraContext  an optional object containing extraContext that's been processed by `api.enhanceExtraContext()`
 * @return {undefined}
 */
export const detectRegressions = (
  jsonNodes: Array<JSONNode>,
  logFn: (arg1: ReportType) => void,
  enhancedExtraContext: EnhancedExtraContext = emptyEnhancedExtraContext
) => {
  if (REGRESSIONS_CHECKS.length === 0) return;

  const dedupedLogFn = dedupeWarnings(logFn);

  // Regression detection happens after the site migrations so we want
  // to always use the app version when running the regression checks.
  const patchVersion = APP_VERSION;

  return processNodes(jsonNodes, (context: Context) => {
    const {node} = context;

    context.extraContext = enhancedExtraContext;

    REGRESSIONS_CHECKS.forEach((patchToApply: NodePatch) => {
      const extraFun = {reportWarning: reportWarning(dedupedLogFn)};

      const {needed, logData} = patchToApply(context, extraFun);

      if (needed) {
        dedupedLogFn(logData);
      }

      if (
        node.jsonNode &&
        enhancedExtraContext.plugins &&
        shouldApplyPatchToDefaultValues(node.jsonNode, patchVersion)
      ) {
        detectRegressionsDefaultValues(
          node.jsonNode,
          patchToApply,
          extraFun,
          enhancedExtraContext,
          reportChange(dedupedLogFn)
        );
      }

      if (
        node.jsonNode &&
        enhancedExtraContext.plugins &&
        shouldApplyPatchToDefaultData(node.jsonNode, patchVersion)
      ) {
        detectRegressionsDefaultData(
          node.jsonNode,
          patchToApply,
          extraFun,
          enhancedExtraContext,
          reportChange(dedupedLogFn)
        );
      }
    });
  });
};

/**
 * Migrates a jsonTree of jsonNodes from `fromVersion` to `toVersion`
 * @param  {Array}    jsonNodes             a jsonTree (flat array) of jsonNodes
 * @param  {number}   fromVersion           site version. eg: 25
 * @param  {number}   toVersion             site version. eg: 25
 * @param  {Function} logFn                 a function to report patches and warnings
 * @param  {Object}   enhancedExtraContext  an optional object containing extraContext that's been processed by `api.enhanceExtraContext()`
 * @return {Array}                          a jsonTree (flat array) of migrated jsonNodes
 */
export const migrateJsonNodes = (
  jsonNodes: Array<JSONNode>,
  fromVersion: number,
  toVersion: number,
  logFn: (arg1: ReportType) => void,
  extraContext: ExtraContext
) => {
  const dedupedLogFn = dedupeWarnings(logFn);
  const enhancedExtraContext = enhanceExtraContext(extraContext);
  let processedNodes: Array<JSONNode> = [];

  processedNodes = preProcessNodes(jsonNodes, dedupedLogFn);

  MIGRATIONS.forEach((patch, i) => {
    const version = i + 1;
    if (patch.node && fromVersion < version && version <= toVersion) {
      processedNodes = applyPatch(
        processedNodes,
        patch.node,
        version,
        dedupedLogFn,
        enhancedExtraContext
      );
    }
  });

  return processedNodes;
};

/**
 * Applies hot patches to a jsonTree of jsonNodes, typically used on the front-end during
 * jsonNode import while a bug is still active to prevent the designer from breaking
 * while the root cause is investigated. Existing patches can be added to this list, or new
 * patches. Once the root cause is fixed, the patch should be removed from here and added
 * to migrateJsonNodes() as part of a new version / migration.
 * @param  {Array}    jsonNodes             a jsonTree (flat array) of jsonNodes
 * @param  {Function} logFn                 a function to report patches and warnings
 * @param  {Object}   enhancedExtraContext  an optional object containing extraContext that's been processed by `api.enhanceExtraContext()`
 * @return {Array}                          a jsonTree (flat array) of migrated jsonNodes
 */
export const applyJsonNodeHotPatches = (
  jsonNodes: Array<JSONNode>,
  logFn: (arg1: ReportType) => void,
  enhancedExtraContext: EnhancedExtraContext = emptyEnhancedExtraContext
): Array<JSONNode> => {
  const dedupedLogFn = dedupeWarnings(logFn);
  // Hot patches are always run after the site migrations so we want
  // to always use the app version when running the hot patches.
  const patchVersion = APP_VERSION;
  return HOT_PATCHES.reduce(
    (patchedNodes, hotPatch) =>
      applyPatch(
        patchedNodes,
        hotPatch,
        patchVersion,
        dedupedLogFn,
        enhancedExtraContext
      ),
    jsonNodes
  );
};

export const applyJsonNodeColdPatches = (
  jsonNodes: Array<JSONNode>,
  fromVersion: number,
  logFn: (arg1: ReportType) => void,
  enhancedExtraContext: EnhancedExtraContext = emptyEnhancedExtraContext
): Array<JSONNode> => {
  const dedupedLogFn = dedupeWarnings(logFn);

  return MIGRATIONS.reduce((processedNodes, patch, i) => {
    const version = i + 1;
    const shouldApplyPatch =
      patch.node && fromVersion < version && version <= APP_VERSION;

    return shouldApplyPatch && patch.node
      ? applyPatch(
          processedNodes,
          patch.node,
          version,
          dedupedLogFn,
          enhancedExtraContext
        )
      : processedNodes;
  }, jsonNodes);
};

/**
 * This cleans a jsonTree from things like cyclical references that would break
 * processNodes iteration. It's typically run as a step before something that calls
 * processNodes().
 * @param  {Array}    jsonNodes a jsonTree (flat array) of jsonNodes
 * @param  {Function} logFn     a function to report patches and warnings
 * @return {Array}              a jsonTree (flat array) of cleaned jsonNodes
 */
export const preProcessNodes = (
  jsonNodes: Array<JSONNode>,
  logFn: (arg1: ReportType) => void
): Array<JSONNode> => {
  if (!jsonNodes || jsonNodes.length === 0) {
    return jsonNodes;
  }
  const duplicates = new Set();
  const nodesWithLostChildrenOrOverrides: Record<string, any> = {};

  // Building a ID -> node map that does not have duplicates.
  const nodesMap = jsonNodes.reduce<Record<string, any>>((map, jsonNode) => {
    const {_id: nodeId} = jsonNode;
    if (map[nodeId]) {
      duplicates.add(nodeId);
    } else {
      map[nodeId] = jsonNode;
    }
    return map;
  }, {});

  if (duplicates.size > 0) {
    logFn({
      msg: 'Remove duplicate nodes',
      meta: {
        duplicates,
      },
    });
  }

  const nodesToProcess: Array<JSONNode> = [];
  const seenNodes: Record<string, any> = {};

  const removeCorruptOverrides = (jsonNode: JSONNode) => {
    const {_id: jsonNodeId} = jsonNode;
    const overrides = jsonNode?.data?.sym?.overrides;

    if (overrides) {
      for (const overrideKey in overrides) {
        if (hasFlattenedOverrides(overrides[overrideKey])) {
          const seenOverrides: Record<string, any> = {};
          overrides[overrideKey] = overrides[overrideKey].filter(
            (overrideId: string) => {
              // We remove already seen overrides to avoid cyclical node trees.
              if (seenNodes[overrideId] || seenOverrides[overrideId]) {
                logFn({
                  msg: 'Remove nth reference to node',
                  meta: {
                    overrideId,
                  },
                });
                return false;
              }
              seenOverrides[overrideId] = true;

              // If the override id does not match any real node, the node is
              // corrupt and we can safely delete the override ID from children.
              if (!nodesMap[overrideId]) {
                nodesWithLostChildrenOrOverrides[jsonNodeId] =
                  nodesWithLostChildrenOrOverrides[jsonNodeId] || [];
                nodesWithLostChildrenOrOverrides[jsonNodeId].push(overrideId);
                return false;
              }

              return true;
            }
          );
        }
      }
    }
  };

  const removeCorruptChildren = (jsonNode: JSONNode) => {
    const {_id: jsonNodeId, children: jsonNodeChildren} = jsonNode;

    if (jsonNodeChildren && jsonNodeChildren.length > 0) {
      const seenChildren: Record<string, any> = {};
      jsonNode.children = jsonNodeChildren.filter((childId) => {
        // We remove already seen children to avoid cyclical node trees.
        if (seenNodes[childId] || seenChildren[childId]) {
          logFn({
            msg: 'Remove nth reference to node',
            meta: {
              childId,
            },
          });
          return false;
        }
        seenChildren[childId] = true;

        // If the child id does not match any real node, the node is
        // corrupt and we can safely delete the child ID from children.
        if (!nodesMap[childId]) {
          nodesWithLostChildrenOrOverrides[jsonNodeId] =
            nodesWithLostChildrenOrOverrides[jsonNodeId] || [];
          nodesWithLostChildrenOrOverrides[jsonNodeId].push(childId);
          return false;
        }

        return true;
      });
    }
  };

  // We want to recursively rebuild the domNodes array
  // so that the order of nodes in the array corresponds
  // the topological order of nodes in the tree.
  const preProcessNode = (jsonNode: JSONNode) => {
    seenNodes[jsonNode._id] = true;

    removeCorruptChildren(jsonNode);
    removeCorruptOverrides(jsonNode);

    nodesToProcess.push(jsonNode);

    if (jsonNode.children && jsonNode.children.length > 0) {
      jsonNode.children.forEach((childId) => {
        const child = nodesMap[childId];
        preProcessNode(child);
      });
    }

    // @NOTE - We currently have an issue where sometimes `LineBreak` Plugin
    // Components have `data.sym.overrides`. This causes the code below to crash.
    // Below we remove `overrides` from `LineBreak`s until we figure out a more
    // permamant fix.
    if (jsonNode.type === 'LineBreak' && jsonNode?.data?.sym?.overrides) {
      // eslint-disable-next-line webflow/no-delete
      delete jsonNode.data.sym.overrides;
    }

    const overrides = jsonNode?.data?.sym?.overrides;

    if (overrides) {
      for (const overrideKey in overrides) {
        if (hasFlattenedOverrides(overrides[overrideKey])) {
          overrides[overrideKey].forEach((f: string) => {
            const overrideJsonNode = nodesMap[f];
            preProcessNode(overrideJsonNode);
          });
        }
      }
    }
  };

  jsonNodes.forEach((node) => {
    if (
      node.tag === 'body' ||
      node.type === 'Body' ||
      (node.data && node.data.sym && node.data.sym.root === true)
    ) {
      preProcessNode(node);
    }
  });

  if (Object.keys(nodesWithLostChildrenOrOverrides).length > 0) {
    logFn({
      msg: 'Remove references to lost children',
      meta: {
        lostChildrenMap: nodesWithLostChildrenOrOverrides,
      },
    });
  }

  return nodesToProcess;
};

/**
 * Runs a tree iteration for a patch, used for hot patches and migrations.
 * @param  {Array}     jsonNodes              a jsonTree (flat array) of jsonNodes
 * @param  {Function}  patchToApply           a patch to apply to each node
 * @param  {Function}  logFn                  a function to report patches and warnings
 * @param  {Object}    enhancedExtraContext   an optional object containing extraContext
 * @return {Array}                            a jsonTree (flat array) of cleaned jsonNodes
 */
export const applyPatch = (
  jsonNodes: Array<JSONNode>,
  patchToApply: NodePatch,
  patchVersion: number,
  logFn: (arg1: ReportType) => void,
  enhancedExtraContext: EnhancedExtraContext = emptyEnhancedExtraContext
) => {
  return processNodes(jsonNodes, (context: Context) => {
    const {node} = context;
    context.extraContext = enhancedExtraContext;

    // We check that node.jsonNode still exists here, if a patch removes the node from the tree
    // no need to keep patching it.
    if (node.jsonNode) {
      const extraFun = {reportWarning: reportWarning(logFn)};

      // Init the patch with this node
      // It's possible we can turn this into a unary function because `extraContext` is already on
      // `context` and includes `reportWarning`.
      const {needed, apply, logData} = patchToApply(context, extraFun);

      // Patch normal jsonNodes first
      if (needed) {
        const newSubtree = apply() || [node.jsonNode];
        node.newSubtree = newSubtree;
        // @ts-expect-error when we're removing a jsonNode, it becomes undefined.
        node.jsonNode = newSubtree.length ? newSubtree[0] : undefined;

        reportChange(logFn)(logData);
      }

      // We have to double check that `node.jsonNode` still exists here since we might
      // have removed it during the `patchToApply` above.
      if (
        node.jsonNode &&
        enhancedExtraContext.plugins &&
        shouldApplyPatchToDefaultValues(node.jsonNode, patchVersion)
      ) {
        applyPatchToDefaultValues(
          node.jsonNode,
          patchToApply,
          extraFun,
          enhancedExtraContext,
          reportChange(logFn)
        );
      }

      if (
        node.jsonNode &&
        enhancedExtraContext.plugins &&
        shouldApplyPatchToDefaultData(node.jsonNode, patchVersion)
      ) {
        applyPatchToDefaultData(
          node.jsonNode,
          patchToApply,
          extraFun,
          enhancedExtraContext,
          reportChange(logFn)
        );
      }
    }

    return context.node.newSubtree;
  });
};

const clampVersion = (maxVersion: number) => (version: number) =>
  version < 0 ? 0 : version > maxVersion ? maxVersion : version;

export const migrateFlow = (migrations: Migrations) => {
  const appVersion = getAppVersion(migrations);
  const clamp = clampVersion(appVersion);

  return (fromVersion: number, toVersion: number) => {
    const from = clamp(fromVersion);
    const to = clamp(toVersion);
    return (flowExpr: Expression): Expression => {
      let i = from;
      let res = flowExpr;
      while (i < to) {
        // @ts-expect-error - TS2345 - Argument of type 'Patch | undefined' is not assignable to parameter of type 'Patch'.
        const patch = getFlowPatch(migrations[i]);
        res = patch(res);
        i++;
      }
      return res;
    };
  };
};

export const getNumericSiteVersion = (version: string | number = 0): number => {
  if (typeof version === 'number') {
    return version;
  }
  return typeof LEGACY_VERSIONS[version] === 'number'
    ? LEGACY_VERSIONS[version]
    : 0;
};
